import { CDN_HOST } from './cdn-host'
import React from 'react'

const MOVIE_GENRES = [
  'Action',
  'Adventure',
  'Comedy',
  'Detective/Noir',
  'Drama',
  'Fantasy',
  'Horror',
  'History',
  'Mystery',
  'Romance',
  'Thriller',
  'Western',
  'Sci-Fi'
].sort((a, b) => a - b)

const PILOT_FORMATS = [
  'Animation',
  'Crime',
  'Reality',
  'Sitcom',
  'Soap operas',
  'TV series',
  'Mini series',
  'Other'
].sort((a, b) => a - b)

const USER_ROLES = [
  'Producer',
  'Screen Writer',
  'Indie Filmmaker',
  'Director',
  'Student',
  'Other'
].sort((a, b) => a - b)

const SOCIAL_PLATFORMS = [
  'TikTok',
  'Instagram Reel'
]

const IMAGE_STYLE_PRESETS = [
  {
    name: 'Sketch',
    value: 'sketch'
  },
  {
    name: 'Cinematic',
    value: 'cinematic'
  },
  {
    name: 'Comic Book',
    value: 'comicBook'
  },
  {
    name: 'Digital Art',
    value: 'digitalArt'
  },
  {
    name: 'Photographic',
    value: 'photographic'
  }
]

const ASPECT_RATIOS = [
  {
    name: '1:1',
    value: '1:1'
  },
  {
    name: '4:3',
    value: '4:3'
  },
  {
    name: '16:9',
    value: '16:9'
  }
]

const COMPETITION_CATEGORIES = [
  'Feature Film',
  'Short film',
  'Episodic Pilot'
]

const COMPETITION_STATUSES = {
  in_review: 'In review',
  accepted: 'Active',
  completed: 'Completed',
  deleted: 'Deleted',
  canceled: 'Canceled'
}

const SHOT_SIZES = [
  {
    label: <span>Close Ups</span>,
    options: [
      {
        label: <span>Close up</span>,
        value: 'close up'
      },
      {
        label: <span>Medium Close-Up</span>,
        value: 'medium close up'
      },
      {
        label: <span>Extreme Close-Up</span>,
        value: 'extreme close up'
      },
      {
        label: <span>Wide Close-Up</span>,
        value: 'wide close up'
      }
    ]
  },
  {
    label: <span>Medium Shots</span>,
    options: [
      {
        label: <span>Medium Shot</span>,
        value: 'medium shot'
      },
      {
        label: <span>Close shot</span>,
        value: 'close shot'
      },
      {
        label: <span>Medium Close Shot</span>,
        value: 'medium close shot'
      }
    ]
  },
  {
    label: <span>Long Shots</span>,
    options: [
      {
        label: <span>Wide Shot</span>,
        value: 'wide shot'
      },
      {
        label: <span>Extreme Wide Shot</span>,
        value: 'extreme wide shot'
      },
      {
        label: <span>Full Shot</span>,
        value: 'full shot'
      },
      {
        label: <span>Medium Full Shot</span>,
        value: 'medium full shot'
      },
      {
        label: <span>Long Shot</span>,
        value: 'long shot'
      },
      {
        label: <span>Extreme Long Shot</span>,
        value: 'extreme long shot'
      }
    ]
  }
]

const OUTFIT_AND_PROPS = {
  basic: {
    outfits: [
      'jeans', 't-shirt', 'hoodie', 'sweater', 'jacket',
      'shorts', 'dress', 'skirt', 'tank top', 'polo shirt',
      'button-up shirt', 'blazer', 'cardigan', 'pants', 'blouse',
      'sweatshirt', 'leggings', 'sweatpants', 'denim jacket', 'crop top',
      'turtleneck', 'jumpsuit', 'romper', 'yoga pants', 'tracksuit'
    ],
    props: [
      'backpack', 'sunglasses', 'watch', 'belt', 'scarf',
      'hat', 'cap', 'glasses', 'wallet', 'purse',
      'messenger bag', 'earphones', 'umbrella', 'gloves', 'necklace',
      'bracelet', 'ring', 'tote bag', 'phone', 'laptop bag',
      'earrings', 'socks', 'beanie', 'shoulder bag', 'headphones'
    ]
  },

  // Formal
  formal: {
    outfits: [
      'suit', 'tuxedo', 'evening gown', 'cocktail dress', 'dress shirt',
      'formal pants', 'bow tie', 'tie', 'vest', 'formal skirt',
      'formal blouse', 'dinner jacket', 'formal coat', 'cummerbund', 'formal jumpsuit',
      'tails', 'formal dress', 'three-piece suit', 'formal suit', 'sheath dress',
      'ball gown', 'formal jacket', 'dress pants', 'formal top', 'waistcoat'
    ],
    props: [
      'cufflinks', 'tie clip', 'pocket square', 'dress shoes', 'clutch',
      'formal watch', 'dress belt', 'formal purse', 'dress socks', 'formal scarf',
      'formal gloves', 'hair clips', 'brooch', 'formal hat', 'formal shawl',
      'dress boots', 'formal jewelry', 'hair pins', 'formal glasses', 'formal bag',
      'boutonniere', 'corsage', 'dress watch', 'formal umbrella', 'opera glasses'
    ]
  },
  // Action/Thriller
  action: {
    outfits: [
      'tactical vest', 'combat boots', 'cargo pants', 'leather jacket', 'bulletproof vest',
      'military uniform', 'stealth suit', 'combat gloves', 'utility belt', 'camouflage jacket',
      'tactical pants', 'combat shirt', 'kevlar vest', 'military boots', 'tactical shirt',
      'combat vest', 'battle suit', 'tactical jacket', 'armored suit', 'combat pants',
      'stealth armor', 'tactical uniform', 'battle gear', 'combat armor', 'special ops suit'
    ],
    props: [
      'handgun', 'combat knife', 'rifle', 'grenades', 'ammunition',
      'night vision goggles', 'radio headset', 'grappling hook', 'flashlight', 'binoculars',
      'tactical backpack', 'laser sight', 'smoke grenades', 'brass knuckles', 'climbing gear',
      'holster', 'bulletproof shield', 'wire cutter', 'tactical watch', 'lockpick set',
      'first aid kit', 'explosive charges', 'compass', 'tactical glasses', 'survival kit'
    ]
  },

  // Period Drama
  period: {
    outfits: [
      'empire waist dress', 'tailcoat', 'top hat', 'corset', 'waistcoat',
      'cravat', 'petticoat', 'morning coat', 'bonnet', 'silk gown',
      'breeches', 'spencer jacket', 'riding habit', 'frock coat', 'ball gown',
      'regency dress', 'silk waistcoat', 'evening coat', 'walking dress', 'military uniform',
      'lace shawl', 'riding coat', 'day dress', 'court dress', 'muslin gown'
    ],
    props: [
      'fan', 'parasol', 'pocket watch', 'walking cane', 'lace gloves',
      'snuff box', 'letter seal', 'calling card case', 'lorgnette', 'reticule',
      'quill pen', 'ink well', 'handkerchief', 'hat box', 'leather bound book',
      'dance card', 'brooch', 'cameo pin', 'locket', 'smelling salts',
      'riding crop', 'tea set', 'opera glasses', 'hair pins', 'diary'
    ]
  },

  // Sci-Fi
  scifi: {
    outfits: [
      'space suit', 'neural suit', 'cyborg armor', 'holographic clothing', 'quantum vest',
      'plasma armor', 'nanotech suit', 'bio-enhanced uniform', 'energy suit', 'gravity boots',
      'power armor', 'stealth cloak', 'cybernetic suit', 'enviro-suit', 'battle mech',
      'phase shift armor', 'quantum shield suit', 'neural interface gear', 'teleport suit', 'bio-mesh armor',
      'energy shield vest', 'nano-fabric suit', 'time suit', 'void armor', 'cosmic suit'
    ],
    props: [
      'laser pistol', 'holographic display', 'neural implant', 'energy shield', 'quantum scanner',
      'plasma rifle', 'teleporter', 'force field generator', 'bio-scanner', 'neural interface',
      'anti-gravity device', 'energy sword', 'holo-communicator', 'matter converter', 'time device',
      'nano-bot injector', 'quantum computer', 'plasma shield', 'cyber-eye', 'neural disruptor',
      'phase shifter', 'energy core', 'data crystal', 'bio-enhancer', 'quantum key'
    ]
  },

  // Fantasy
  fantasy: {
    outfits: [
      'wizard robe', 'elven cloak', 'dragon scale armor', 'mage robes', 'enchanted cloak',
      'leather armor', 'mystical dress', 'royal garments', 'druid robes', 'ranger cloak',
      'paladin armor', 'sorceress gown', 'battle mage armor', 'fairy dress', 'assassin garb',
      'mythril armor', 'enchanted dress', 'shadow cloak', 'celestial robe', 'warrior armor',
      'magical suit', 'royal armor', 'mystic vest', 'crystal armor', 'astral robes'
    ],
    props: [
      'magic wand', 'enchanted sword', 'crystal staff', 'spell book', 'magic ring',
      'potion bag', 'magical orb', 'dragon egg', 'enchanted bow', 'mystic amulet',
      'healing stones', 'magic scrolls', 'fairy dust', 'dragon scale shield', 'wizard staff',
      'rune stones', 'magic compass', 'soul gem', 'enchanted dagger', 'magic map',
      'crystal ball', 'magical herbs', 'phoenix feather', 'dragon horn', 'magic mirror'
    ]
  }
}

const AVAILABLE_FOR_SHARED_PROJECT = [
  'screenplay',
  'breakdown',
  'storyboard',
  'storyboard-workflow',
  'scheduling',
  'budgeting-forecast',
  'pitch-deck'
]

export {
  MOVIE_GENRES,
  PILOT_FORMATS,
  USER_ROLES,
  CDN_HOST,
  SOCIAL_PLATFORMS,
  IMAGE_STYLE_PRESETS,
  ASPECT_RATIOS,
  COMPETITION_CATEGORIES,
  COMPETITION_STATUSES,
  SHOT_SIZES,
  OUTFIT_AND_PROPS,
  AVAILABLE_FOR_SHARED_PROJECT
}
